import { types } from "mobx-state-tree";

export enum PublishStatus {
  Draft = "Draft",
  Preview = "Preview",
  Published = "Published",
  ScheduleForRemove = "ScheduleForRemove",
}

export const SimpleModel = types.model({
  id: types.number,
  name: types.maybe(types.string),
});

export type SimpleModelType = {
  id: number;
  name: string;
};

export const DictionaryItemModel = types
  .model({
    id: types.number,
    name: types.maybe(types.string),
  })
  .views(self => ({
    get asDictionaryItem(): DictionaryItem {
      return { label: self.name || "", value: `${self.id}` };
    },
  }));

export type DictionaryItem = {
  value: any;
  label: string;
};

export const PROGRAM_PERMISSION_TYPES = {
  PREVIEW: "Preview",
  FULL_ACCESS: "FullAccess",
};

export const ActiveProgram = types.model({
  id: types.number,
  name: types.maybe(types.string),
  permission: types.enumeration(Object.values(PROGRAM_PERMISSION_TYPES)),
});

export const STRUCTURE_TYPES = {
  STANDARD: "Standard",
  WEEK: "Week",
};

export const WORKOUT_STRUCTURE_TYPES = {
  STANDARD: "Standard",
  WEEK: "Week",
  COLLECTION: "Collection",
};

export const FOOD_PROGRAM_TYPES = {
  UNKNOWN: "Unknown",
  VIP_AND_OTHER: "VipAndOther",
  SIX_WEEK: "SixWeek",
};

export const PUBLISH_STATUSES = {
  DRAFT: "Draft",
  PREVIEW: "Preview",
  PUBLISHED: "Published",
  SCHEDULE_FOR_REMOVE: "ScheduleForRemove",
};

export const ACTIVE_PROGRAM_TYPE = {
  COMMON: "Common",
  VIP: "Vip",
  SPECIAL: "Special",
  COACH: "Coach",
};

export const ACTIVE_PROGRAM_TYPE_TEXT = {
  COMMON: "6-Week",
  VIP: "Vip",
  SPECIAL: "Special",
  COACH: "Coach",
};

export const PUBLISH_STATUSES_TEXT = {
  DRAFT: "Draft",
  PREVIEW: "Preview",
  PUBLISHED: "Published",
  SCHEDULE_FOR_REMOVE: "Scheduled to remove",
};

export const RESPONSE_TYPES = {
  TEXT: "Text",
  EMAIL: "Email",
  UNIQUE: "Unique",
  MULTIPLE: "Multiple",
  TEXT_AREA: "TextArea",
  NUMBER: "Number",
  DECIMAL: "Decimal",
  CALENDAR: "Calendar",
};

export const RESPONSE_TYPES_TEXT = {
  TEXT: "Text",
  EMAIL: "Email",
  UNIQUE: "Unique",
  MULTIPLE: "Multiple",
  TEXT_AREA: "Text area",
  NUMBER: "Number",
  DECIMAL: "Decimal",
  CALENDAR: "Calendar",
};

export const PUBLISH_STATUSES_BUTTON_TEXT = {
  DRAFT: "Publish to Preview",
  DRAFT_MANUAL_BADGE: "Publish to Prod",
  PREVIEW: "Unpublish from Preview",
  PUBLISHED: "Unpublish from Prod",
  SCHEDULE_FOR_REMOVE: "Publish to Prod",
};

export const RELATED_DATA = {
  ARM: "arm",
  BUST: "bust",
  BUTT: "butt",
  DATE_OF_BIRTH: "date_of_birth",
  EMAIL: "email",
  GENDER: "gender",
  HIPS: "hips",
  HEIGHT: "height",
  LOCATION: "location",
  NAME: "name",
  PHONE: "phone",
  THIGH: "thigh",
  WAIST: "waist",
  WEIGHT: "weight",
  ACTIVITY_LEVEL: "activity_level",
};

export const RELATED_DATA_TEXT = {
  ARM: "Arm",
  BUST: "Bust",
  BUTT: "Butt",
  DATE_OF_BIRTH: "Date of Birth",
  EMAIL: "Email",
  GENDER: "Gender",
  HIPS: "Hips",
  HEIGHT: "Height",
  LOCATION: "Location",
  NAME: "Name",
  PHONE: "Phone",
  THIGH: "Thigh",
  WAIST: "Waist",
  WEIGHT: "Weight",
};

export const CategoryModel = types.model({
  id: types.number,
  title: types.string,
  subTitle: types.maybe(types.string),
  note: types.maybe(types.string),
  imageUrl: types.maybe(types.string),
  structureType: types.enumeration(Object.values(STRUCTURE_TYPES)),
  publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
});

export const WorkoutCategoryModel = types.model({
  id: types.number,
  title: types.string,
  subTitle: types.maybeNull(types.string),
  note: types.maybeNull(types.string),
  imageUrl: types.maybeNull(types.string),
  publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
  structureType: types.enumeration(Object.values(WORKOUT_STRUCTURE_TYPES)),
  startDate: types.maybeNull(types.string),
  activePrograms: types.maybe(types.array(ActiveProgram)),
  order: types.maybe(types.number),
  family: types.maybe(
    types.model({
      id: types.number,
      structureType: types.maybe(
        types.enumeration(Object.values(WORKOUT_STRUCTURE_TYPES)),
      ),
      name: types.maybeNull(types.string),
    }),
  ),
});

export const NutritionCategoryModel = types.model({
  id: types.number,
  title: types.string,
  subTitle: types.maybe(types.string),
  note: types.maybe(types.string),
  imageUrl: types.maybe(types.string),
  foodProgramType: types.enumeration(Object.values(FOOD_PROGRAM_TYPES)),
  structureType: types.enumeration(Object.values(STRUCTURE_TYPES)),
  publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
});

export const QuickLinkModel = types
  .model({
    id: types.number,
    originalQuickLinkId: types.maybe(types.number),
    title: types.string,
    imageUrl: types.maybe(types.string),
    created: types.maybe(types.string),
    order: types.maybe(types.number),
    activePrograms: types.array(ActiveProgram),
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
  })
  .views(self => ({
    get accessFormData() {
      return {
        activePrograms: self.activePrograms.map(({ id, name, permission }) => ({
          id,
          name,
          switchState: permission === PROGRAM_PERMISSION_TYPES.PREVIEW,
        })),
      };
    },
  }));
